import { useRef } from 'react';
import { Printer, Zap, EyeOff, Eye } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import type { BvBill, CalcExclusion, ShadowBillResult, ShadowBillSection, ShadowBillLine } from '../../types/billTypes';
import { applyExclusions, excludedSummary } from '../../utils/calcExclusions';
import { vocabularyNameFor } from '../../utils/expectedVocabulary';

type Props = {
  bill: BvBill;
  result: ShadowBillResult;
  compact?: boolean;
  /** Job 53: charges switched out of the expected invoice. When toggle handlers
   *  are supplied the document renders per-charge/per-section on-off controls and
   *  strikes excluded rows through; otherwise it just reflects them in the totals. */
  exclusions?: CalcExclusion[];
  onToggleLine?: (line: ShadowBillLine) => void;
  onToggleSection?: (section: ShadowBillSection) => void;
};

function fmt(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
}

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// How each section's amounts are derived, shown inline so the figures can be
// checked without reading the calculation service.
const SECTION_NOTES: Partial<Record<ShadowBillSection, string>> = {
  retail:
    'Usage × rate × DLF × MLF, using the retailer TOU windows. Supply charge is the contracted rate '
    + 'converted to a daily amount × billing days.',
  distribution:
    'Usage × network rate, using the distributor TOU windows (different to retail). Loss factors are not '
    + 'applied — network rates are already at the connection point. Demand = kVA × rate × days ÷ 365.',
  metering:
    'Meter Data Service = contracted annual metering fee ÷ 365 × billing days. Meter Rental = daily rate × '
    + 'billing days, and is only charged from metering-only rate rows — a supply charge on a row that also '
    + 'carries energy rates is billed once, under Retail.',
  environmental:
    'Total usage × certificate rate × DLF. MLF is not applied. Only certificates whose period covers the '
    + 'billing period are included.',
};

const SECTIONS: { key: ShadowBillSection; label: string; headerBg: string; headerText: string }[] = [
  { key: 'retail',        label: 'Retail Energy Charges',       headerBg: 'bg-blue-700',   headerText: 'text-white' },
  { key: 'distribution',  label: 'Network / Distribution Charges', headerBg: 'bg-indigo-700', headerText: 'text-white' },
  { key: 'metering',      label: 'Metering Charges',            headerBg: 'bg-orange-700', headerText: 'text-white' },
  { key: 'environmental', label: 'Environmental Charges',        headerBg: 'bg-green-700',  headerText: 'text-white' },
  { key: 'gst',           label: 'GST',                         headerBg: 'bg-gray-600',   headerText: 'text-white' },
];

function SectionTable({
  sectionLines,
  compact,
  onToggleLine,
}: {
  sectionLines: ShadowBillLine[];
  compact?: boolean;
  onToggleLine?: (line: ShadowBillLine) => void;
}) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-gray-500 border-b border-gray-200">
          {onToggleLine && <th className="py-1.5 pr-2 font-medium no-print w-8" />}
          <th className={cn('py-1.5 font-medium', compact ? 'pr-2' : 'pr-4')}>Description</th>
          {!compact && <th className="py-1.5 text-right pr-4 font-medium">QTY</th>}
              {!compact && <th className="py-1.5 text-right pr-4 font-medium">Usage</th>}
          {!compact && <th className="py-1.5 text-right pr-4 font-medium">Rate (c)</th>}
          {!compact && <th className="py-1.5 text-right pr-4 font-medium">DLF</th>}
          {!compact && <th className="py-1.5 text-right pr-4 font-medium">MLF</th>}
          {!compact && <th className="py-1.5 text-right pr-4 font-medium">Adj Rate</th>}
          <th className="py-1.5 text-right font-medium">Amount</th>
        </tr>
      </thead>
      <tbody>
        {sectionLines.map((l, i) => {
          const excluded = l.excluded === true;
          // Both sides of a comparison speak the charge map's vocabulary. The
          // retailer side is renamed to its `expected_item` on a map hit, so the
          // expected side prints the same vocabulary name wherever the name can
          // be placed — a name that cannot keeps the calculation's own wording.
          // Display only: `shadow_result` still holds `l.description`.
          const vocabName = vocabularyNameFor(l.description);
          return (
            <tr
              key={i}
              className={cn(
                'border-b border-gray-100',
                i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50',
                excluded && 'opacity-50',
              )}
            >
              {onToggleLine && (
                <td className="py-1.5 pr-2 no-print">
                  <button
                    type="button"
                    onClick={() => onToggleLine(l)}
                    title={excluded ? 'Include this charge' : 'Exclude this charge'}
                    className={cn(
                      'inline-flex items-center justify-center h-5 w-5 rounded hover:bg-gray-200',
                      excluded ? 'text-gray-400' : 'text-blue-600',
                    )}
                  >
                    {excluded ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </td>
              )}
              <td
                className={cn('py-1.5 text-gray-700', compact ? 'pr-2' : 'pr-4', excluded && 'line-through text-gray-400')}
                title={vocabName && vocabName !== l.description
                  ? `Calculated as "${l.description}" — shown under its charge-map name.`
                  : undefined}
              >
                {vocabName ?? l.description}
              </td>
              {!compact && (
                <td className="py-1.5 text-right pr-4 text-gray-600 tabular-nums">
                  {l.volume != null ? `${l.volume.toLocaleString()} ${l.volume_unit ?? ''}` : '—'}
                </td>
              )}
              {/* Usage is its own column, not a fallback for QTY: a metering line
                  is a meter count AND a number of days, and folding them into one
                  cell is what lost the second figure. */}
              {!compact && (
                <td className="py-1.5 text-right pr-4 text-gray-600 tabular-nums">
                  {l.usage != null ? `${l.usage.toLocaleString()} ${l.usage_unit ?? ''}` : '—'}
                </td>
              )}
              {!compact && <td className="py-1.5 text-right pr-4 text-gray-600 tabular-nums">{l.rate != null ? l.rate.toFixed(4) : '—'}</td>}
              {!compact && <td className="py-1.5 text-right pr-4 text-gray-600 tabular-nums">{l.dlf != null ? l.dlf.toFixed(4) : '—'}</td>}
              {!compact && <td className="py-1.5 text-right pr-4 text-gray-600 tabular-nums">{l.mlf != null ? l.mlf.toFixed(4) : '—'}</td>}
              {!compact && <td className="py-1.5 text-right pr-4 text-gray-600 tabular-nums">{l.adj_rate != null ? l.adj_rate.toFixed(4) : '—'}</td>}
              <td className={cn('py-1.5 text-right tabular-nums font-medium', excluded ? 'line-through text-gray-400' : l.amount < 0 ? 'text-green-700' : 'text-gray-900')}>
                {fmt(l.amount)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function ShadowBillDocument({
  bill,
  result: rawResult,
  compact = false,
  exclusions,
  onToggleLine,
  onToggleSection,
}: Props) {
  const printRef = useRef<HTMLDivElement>(null);

  // Job 53: reflect the exclusions in the totals and flag the excluded lines.
  // The lines are never dropped, so an omission from a total is always visible.
  const result = applyExclusions(rawResult, exclusions);
  const summary = excludedSummary(rawResult, exclusions);

  const handlePrint = () => {
    const content = printRef.current?.innerHTML ?? '';
    const safeName = (bill.site?.nmi ?? 'Bill').replace(/[<>&"']/g, '');
    const win = window.open('', '_blank', 'width=1000,height=750,scrollbars=yes');
    if (!win) return;
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Expected Invoice — ${safeName}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
          body { background: white; color: #111; font-size: 12px; padding: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 6px 10px; border-bottom: 1px solid #e5e7eb; }
          .bg-white { background: white; }
          .text-white { color: white; }
          .font-bold { font-weight: 700; }
          .font-semibold { font-weight: 600; }
          .font-medium { font-weight: 500; }
          .font-mono { font-family: monospace; }
          .text-right { text-align: right; }
          .border-b { border-bottom: 1px solid #e5e7eb; }
          .py-1\\.5 { padding-top: 6px; padding-bottom: 6px; }
          .px-6 { padding-left: 24px; padding-right: 24px; }
          .py-5 { padding-top: 20px; padding-bottom: 20px; }
          @media print { @page { margin: 12mm; size: A4; } }
        </style>
      </head>
      <body>${content}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.onload = () => win.print();
  };

  const grouped: Partial<Record<ShadowBillSection, ShadowBillLine[]>> = {};
  for (const l of result.lines) {
    if (!grouped[l.section]) grouped[l.section] = [];
    grouped[l.section]!.push(l);
  }

  const nmi = bill.site?.nmi ?? '—';
  const orgName = bill.site?.client?.name ?? '—';

  return (
    <div className="shadow-bill-wrapper">
      {/* Print button — hidden in print */}
      {!compact && (
        <div className="flex justify-end mb-3 no-print">
          <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
            <Printer className="h-4 w-4" />
            Print / Export PDF
          </Button>
        </div>
      )}

      <div ref={printRef} className={cn('bg-white rounded-xl overflow-hidden border border-gray-200', compact && 'text-xs')}>
        {/* ── Document Header ─────────────────────────────────────────────── */}
        <div
          className="px-6 py-5 text-white"
          style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)' }}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Zap className="h-5 w-5 text-blue-300" />
                <span className="text-blue-200 text-xs font-medium uppercase tracking-widest">Expected Invoice</span>
              </div>
              <h1 className={cn('font-bold text-white', compact ? 'text-lg' : 'text-2xl')}>
                Bill Validation Statement
              </h1>
              <p className="text-blue-200 text-xs mt-1">
                This is a calculated expected invoice for validation purposes only
              </p>
            </div>
            <div className="text-right">
              <div className={cn('font-bold text-white tabular-nums', compact ? 'text-xl' : 'text-3xl')}>
                {fmt(result.total)}
              </div>
              <div className="text-blue-200 text-xs mt-0.5">Expected Total (incl. GST)</div>
              <div className="text-blue-300 text-xs mt-2">
                Calculated {new Date(result.calculated_at).toLocaleString('en-AU')}
              </div>
            </div>
          </div>
        </div>

        {/* ── Billing Period Banner ────────────────────────────────────────── */}
        <div className="bg-blue-50 border-b border-blue-100 px-6 py-2.5 flex flex-wrap items-center gap-4 text-sm">
          <span className="font-semibold text-blue-900">Billing Period:</span>
          <span className="text-blue-800">{fmtDate(result.bill_from)} – {fmtDate(result.bill_to)}</span>
          <span className="text-blue-600">({result.billing_days} days)</span>
          <span className="ml-auto text-blue-700 font-medium">Total Usage: {result.total_kwh.toLocaleString()} kWh</span>
        </div>

        {/* ── Account Details ──────────────────────────────────────────────── */}
        <div className={cn('grid grid-cols-2 gap-0 border-b border-gray-200', compact && 'text-xs')}>
          <div className="px-6 py-4 border-r border-gray-200">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Account Details</h3>
            <dl className="space-y-1.5">
              <div className="flex justify-between">
                <dt className="text-gray-500">NMI</dt>
                <dd className="font-mono font-semibold text-gray-900">{nmi}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Entity</dt>
                <dd className="font-medium text-gray-900">{orgName}</dd>
              </div>
            </dl>
          </div>
          <div className="px-6 py-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Consumption Summary</h3>
            <dl className="space-y-1.5 text-sm">
              {bill.peak_kwh != null && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Peak</dt>
                  <dd className="tabular-nums text-gray-900">{bill.peak_kwh.toLocaleString()} kWh</dd>
                </div>
              )}
              {bill.shoulder_kwh != null && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Shoulder</dt>
                  <dd className="tabular-nums text-gray-900">{bill.shoulder_kwh.toLocaleString()} kWh</dd>
                </div>
              )}
              {bill.off_peak_kwh != null && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Off-Peak</dt>
                  <dd className="tabular-nums text-gray-900">{bill.off_peak_kwh.toLocaleString()} kWh</dd>
                </div>
              )}
              {bill.feed_in_kwh != null && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Feed-in</dt>
                  <dd className="tabular-nums text-green-700">−{bill.feed_in_kwh.toLocaleString()} kWh</dd>
                </div>
              )}
              {bill.peak_demand_kva != null && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Peak Demand</dt>
                  <dd className="tabular-nums text-gray-900">{bill.peak_demand_kva} kVA</dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* ── Exclusions summary (job 53) ──────────────────────────────────── */}
        {summary.count > 0 && (
          <div className="bg-amber-50 border-b border-amber-100 px-6 py-2.5 flex items-center gap-2 text-xs text-amber-900">
            <EyeOff className="h-3.5 w-3.5 shrink-0" />
            <span>
              <span className="font-semibold">{summary.count} charge{summary.count !== 1 ? 's' : ''} excluded</span>
              {' '}from the expected invoice — {fmt(summary.total)} left out. Excluded rows are struck through below.
            </span>
          </div>
        )}

        {/* ── Charge Sections ──────────────────────────────────────────────── */}
        <div className="divide-y divide-gray-200">
          {SECTIONS.map(({ key, label, headerBg, headerText }) => {
            const lines = grouped[key];
            if (!lines?.length) return null;
            const sectionTotal = result.section_totals[key] ?? 0;
            const allExcluded = lines.every((l) => l.excluded === true);
            return (
              <div key={key}>
                <div className={cn('px-6 py-2 flex items-center justify-between gap-2', headerBg)}>
                  <div className="flex items-center gap-2">
                    {onToggleSection && key !== 'gst' && (
                      <button
                        type="button"
                        onClick={() => onToggleSection(key)}
                        title={allExcluded ? 'Include this whole section' : 'Exclude this whole section'}
                        className={cn('no-print inline-flex items-center justify-center h-5 w-5 rounded hover:bg-white/20', headerText)}
                      >
                        {allExcluded ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    <h3 className={cn('text-xs font-semibold uppercase tracking-wider', headerText)}>{label}</h3>
                  </div>
                  <span className={cn('text-sm font-bold tabular-nums', headerText)}>{fmt(sectionTotal)}</span>
                </div>
                <div className="px-6 py-3">
                  <SectionTable sectionLines={lines} compact={compact} onToggleLine={key !== 'gst' ? onToggleLine : undefined} />
                  {SECTION_NOTES[key] && (
                    <p className="mt-2 text-[10px] leading-snug text-gray-400">
                      <span className="font-semibold text-gray-500">How this is calculated: </span>
                      {SECTION_NOTES[key]}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Totals ───────────────────────────────────────────────────────── */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-5">
          <div className="max-w-xs ml-auto space-y-2">
            {(['retail', 'distribution', 'metering', 'environmental'] as ShadowBillSection[]).map((s) => {
              const v = result.section_totals[s];
              if (!v) return null;
              return (
                <div key={s} className="flex justify-between text-sm">
                  <span className="text-gray-500 capitalize">{s}</span>
                  <span className="tabular-nums text-gray-700">{fmt(v)}</span>
                </div>
              );
            })}
            <div className="flex justify-between text-sm pt-2 border-t border-gray-300">
              <span className="text-gray-600 font-medium">Subtotal (excl. GST)</span>
              <span className="tabular-nums font-medium text-gray-800">{fmt(result.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">GST (10%)</span>
              <span className="tabular-nums text-gray-800">{fmt(result.gst)}</span>
            </div>
            <div className="flex justify-between pt-3 border-t-2 border-gray-900">
              <span className="text-gray-900 font-bold text-base">TOTAL</span>
              <span className="tabular-nums font-bold text-gray-900 text-xl">{fmt(result.total)}</span>
            </div>
          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        <div className="px-6 py-3 border-t border-gray-100 bg-blue-50/30">
          <p className="text-xs text-gray-400 text-center">
            Expected invoice generated by Bill Validation — for validation purposes only.
            All figures are calculated and may differ from the actual retailer invoice.
          </p>
        </div>
      </div>
    </div>
  );
}
